import { useCallback, useMemo, useState } from 'react'
import textsData from '@/textData/CreateIncidentTexts.json'
import styled from 'styled-components'
import { Col, Container, Row } from 'react-grid-system'
import { Stepper } from '../molecules'
import { Button } from '../atoms'
import { useNavigate } from 'react-router'
import { FetchResult } from '@apollo/client'
import {
  CreateIncidentMutation,
  FileOriginOption,
  FileTypeOption,
  useCreateIncidentMutation,
  useGetFileUploadUrlsMutation,
} from '@/graphql/generated'
import { CreateIncidentTemplate } from '../templates'
import { useCreateIncidentContext } from '@/customHooks/useCreateIncidentContext'
import {
  useErrorHandlerContext,
  useOpeningElementsControllerContext,
  useUserLanguage,
} from '@/customHooks'

const StepperContainer = styled(Col)`
  display: flex;
  justify-content: center;
  padding-top: ${({ theme }) => theme.spacing.md};
  padding-bottom: ${({ theme }) => theme.spacing.md};
`

const ButtonsContainer = styled(Row)`
  margin-top: 3rem;
  margin-bottom: 2rem;
`
export const CreateIncidentPage = () => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es : textsData.en

  const [currentPage, setCurrentPage] = useState(0)
  const [isLoading, setIsloading] = useState(false)
    
  const steps = useMemo(() => texts.templateText.map((element) => element.step), [])

  const { confirmDialogModalOpen } = useOpeningElementsControllerContext()

  const navigate = useNavigate()

  const { state, buttonState, pictures, videos } = useCreateIncidentContext()

  const { open: errorHandle} = useErrorHandlerContext()

  const [getUploadUrls] = useGetFileUploadUrlsMutation()
  const [createIncident] = useCreateIncidentMutation()
  
  
  const onFinishHandle = useCallback(
    async () => {
      const onSuccesHandle = (response: FetchResult<CreateIncidentMutation>) => {
        confirmDialogModalOpen({
          title: texts.confirmationModal.title,
          description:`${texts.confirmationModal.description}${response.data?.createIncident.id}`
        })
        navigate(-1)   
      }

      try {
        setIsloading(true);
        let uploadedFilesIds: string[] = [];

        if (pictures.length > 0) {
          const getUploadUrlsResponse = await getUploadUrls({
            variables: {
              inputs: pictures.map((picture) => ({
                contentType: picture.type,
                fileExtension: 'webp',
                fileTypeOption: FileTypeOption.Picture,
                fileOriginOption: FileOriginOption.Incident,
                fileSize: picture.size.toString(),
              })),
            },
          });

          const uploadDataList = getUploadUrlsResponse.data?.getUploadUrls;
          if (!uploadDataList || uploadDataList.length !== pictures.length) {
            throw new Error('No se pudieron obtener las URLs para subir las imágenes.');
          }

          const uploadPromises = uploadDataList.map((uploadData, index) => {
            const picture = pictures[index];
            const uploadUrl = uploadData.uploadUrl ?? '';
            return fetch(uploadUrl, {
              method: 'PUT',
              body: picture,
              headers: { 'Content-Type': picture.type },
            });
          });

          const uploadResponses = await Promise.all(uploadPromises);

          uploadResponses.forEach((response) => {
            if (!response.ok) {
              throw new Error('Falló la subida de una o más imágenes.');
            }
          });

          uploadedFilesIds = uploadDataList.map(uploadData => uploadData.id);
        }

        if (videos.length > 0) {
          const getUploadUrlsResponse = await getUploadUrls({
            variables: {
              inputs: videos.map((video) => ({
                contentType: 'video/mp4',
                fileExtension: 'mp4',
                fileTypeOption: FileTypeOption.Video,
                fileOriginOption: FileOriginOption.Incident,
                fileSize: video.size.toString(),
              })),
            },
          });

          const uploadDataList = getUploadUrlsResponse.data?.getUploadUrls;
          if (!uploadDataList || uploadDataList.length !== videos.length) {
            throw new Error('No se pudieron obtener las URLs para subir los videos.');
          }

          const uploadPromises = uploadDataList.map((uploadData, index) => {
            const video = videos[index];
            const uploadUrl = uploadData.uploadUrl ?? '';
            return fetch(uploadUrl, {
              method: 'PUT',
              body: video,
              headers: { 'Content-Type': 'video/mp4' },
            });
          });

          const uploadResponses = await Promise.all(uploadPromises);

          uploadResponses.forEach((response) => {
            if (!response.ok) {
              throw new Error('Falló la subida de uno o más videos.');
            }
          });

          uploadedFilesIds = uploadedFilesIds.concat(uploadDataList.map(uploadData => uploadData.id));
        }

        const createdIncident = await createIncident({ variables: { input: { ...state, uploadedFilesIds } } });
        onSuccesHandle(createdIncident)
      } catch (error: any) {
        errorHandle(error);
      } finally {
        setIsloading(false);
      }
    },
    [
      pictures,
      videos,
      state,
      getUploadUrls,
      createIncident,
      errorHandle,
    ],
  );

  return (
    <Container>
      <Row>
        <StepperContainer>
          <Stepper
            steps={steps}
            currentStep={currentPage}
          />
        </StepperContainer>
      </Row>

      <CreateIncidentTemplate currentTemplate={currentPage} />
      
      <ButtonsContainer justify="center">
        <Col xs="content">
          <Button
            secondary
            onClick={() => {
              if (currentPage > 0) setCurrentPage((prev) => prev - 1)
              else navigate(-1)
            }}
            disabled={isLoading}
          >
            {texts.cancelButton}
          </Button>
        </Col>
        <Col xs="content">
          <Button
            onClick={() => {
              if (currentPage < steps.length - 1) setCurrentPage((prev) => prev + 1)
              else onFinishHandle()
            }}
            disabled={buttonState}
            isLoading={isLoading}
          >
            {texts.confirmButton}
          </Button>
        </Col>
      </ButtonsContainer>
    </Container>
  )
}
